import type { CollectionItem, PublicRelease } from "../api/types";
import {
  COLLECTION_DEFAULT_SORT,
  type CollectionUiSort,
} from "./collection-sort";

export type CollectionRarityFilter = "" | "TH" | "STH" | "Exclusive";

export type CollectionViewFilters = {
  query: string;
  year: string;
  series: string;
  rarity: CollectionRarityFilter;
  sort: CollectionUiSort;
};

export function defaultCollectionViewFilters(): CollectionViewFilters {
  return {
    query: "",
    year: "",
    series: "",
    rarity: "",
    sort: COLLECTION_DEFAULT_SORT,
  };
}

export function collectionViewFingerprint(filters: CollectionViewFilters): string {
  return `${filters.query.trim()}|${filters.year}|${filters.series}|${filters.rarity}|${filters.sort}`;
}

export function ownedYears(items: CollectionItem[]): number[] {
  const years = new Set<number>();
  for (const item of items) {
    years.add(item.release.year);
  }
  return [...years].sort((a, b) => b - a);
}

export function ownedSeriesNames(items: CollectionItem[], year: number): string[] {
  const names = new Set<string>();
  for (const item of items) {
    if (item.release.year !== year) {
      continue;
    }
    const name = item.release.seriesName?.trim() ?? "";
    if (name !== "") {
      names.add(name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b, "en"));
}

export function matchesRarity(
  release: Pick<PublicRelease, "huntType" | "isExclusive">,
  rarity: CollectionRarityFilter,
): boolean {
  switch (rarity) {
    case "":
      return true;
    case "TH":
      return release.huntType === "TH";
    case "STH":
      return release.huntType === "STH";
    case "Exclusive":
      return release.isExclusive;
  }
}

export function matchesCollectionFilters(
  item: CollectionItem,
  filters: Omit<CollectionViewFilters, "query" | "sort">,
): boolean {
  if (filters.year !== "" && item.release.year !== Number(filters.year)) {
    return false;
  }
  if (filters.series !== "" && (item.release.seriesName ?? "") !== filters.series) {
    return false;
  }
  if (!matchesRarity(item.release, filters.rarity)) {
    return false;
  }
  return true;
}
